import type { Folder, Note } from "@hq/contracts";
import { descendantFolderIds, orderChanges, wouldCreateCycle } from "@hq/core";
import { useFolders, useNotes, useServices, useLiveQuery, useStore } from "@hq/client-react";
import type { Services } from "@hq/client";
import { useDeleteConfirmation, cn, DropdownMenu, toast } from "@z/ui";
import {
  type ContextMenuItem,
  type ContextMenuOpenContext,
  type FileTree as NotesTreeModel,
  type FileTreeDirectoryHandle,
  type FileTreeDropContext,
  type FileTreeRenameEvent,
  preparePresortedFileTreeInput,
} from "@pierre/trees";
import { FileTree, useFileTree, useFileTreeSelector } from "@pierre/trees/react";
import {
  ArrowDown,
  ArrowUp,
  FilePlus,
  FolderInput,
  FolderPlus,
  PanelsTopLeft,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  type CSSProperties,
  type Ref,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FolderPickerDialog } from "../FolderPickerDialog";
import { displayTitle } from "../lib";
import {
  buildTreePaths,
  folderReorderPlan,
  parentPathOf,
  resolveDrop,
  resolveRename,
  siblingReorderPaths,
  type TreeEntry,
  type TreePaths,
} from "./paths";

export type { NotesTreeModel };

export type NotesTreeProps = {
  selectedNoteId: string | null;
  selectedFolderId?: string | null;
  search?: boolean;
  /** Folders and notes directly beneath the sidebar’s Notes shortcuts. */
  variant?: "default" | "navigation";
  /** Click or Enter opens a preview; navigation double-click asks to keep the tab. */
  onOpenNote: (noteId: string, options?: { pinned: boolean }) => void;
  /** A folder row was selected. */
  onOpenFolder?: (folderId: string, options?: { pinned: boolean }) => void;
  ref?: Ref<NotesTreeHandle>;
  className?: string;
};

export type NotesTreeHandle = {
  /** Opens the tree's inline search; bind it to an app shortcut. */
  openSearch: (initialValue?: string) => void;
  /** Starts an inline rename of a folder or note; false when it is not in the tree. */
  startRenaming: (id: string) => boolean;
  /** Creates a top-level folder and starts inline renaming. */
  createFolder: () => Promise<void>;
  /** Allows the section heading to receive moves to the top level. */
  canDropAtRoot: () => boolean;
  dropAtRoot: () => Promise<void>;
  /** The underlying `@pierre/trees` model, for the app shell and tests. */
  model: NotesTreeModel;
};

const REORDER_CSS = `
[data-item-path], button, [role="button"] { cursor: pointer; }
input { cursor: text; }
[data-item-rename-input] {
  box-sizing: border-box;
  min-height: 26px;
  padding: 2px 6px;
  border: 1px solid var(--trees-accent);
  border-radius: 4px;
  background: var(--z-color-background);
  color: var(--z-color-text);
  outline: none;
}
[data-reorder-edge="before"] { box-shadow: inset 0 2px var(--trees-accent); }
[data-reorder-edge="after"] { box-shadow: inset 0 -2px var(--trees-accent); }
`;

const NEW_FOLDER_NAME = "Untitled folder";
const LIBRARY_ROOT = "Library/";
// The same outlined folder shape used by the Projects navigation, inside Trees' shadow DOM.
const FOLDER_ICON = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5l2 2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2Z"/></svg>')}")`;

/** Tree colours and metrics come from our tokens; every value is a `--trees-theme-*` or override variable. */
const HOST_STYLE = {
  height: "100%",
  minHeight: 0,
  "--trees-theme-sidebar-bg": "var(--z-color-surface)",
  "--trees-theme-sidebar-fg": "var(--z-color-text)",
  "--trees-theme-sidebar-header-fg": "var(--z-color-text-muted)",
  "--trees-theme-sidebar-border": "var(--z-color-border)",
  "--trees-theme-list-active-selection-bg": "var(--z-color-accent-muted)",
  "--trees-theme-list-active-selection-fg": "var(--z-color-text)",
  "--trees-theme-list-hover-bg": "color-mix(in oklab, var(--z-color-text) 6%, transparent)",
  "--trees-theme-focus-ring": "var(--z-color-focus-ring)",
  "--trees-theme-input-bg": "var(--z-color-background)",
  "--trees-theme-input-fg": "var(--z-color-text)",
  "--trees-accent-override": "var(--z-color-accent)",
  "--trees-font-family-override": "inherit",
} as CSSProperties;

/** Align top-level tree rows with the sidebar shortcuts. */
const NAVIGATION_CSS = `
[data-file-tree-virtualized-scroll="true"] { padding-inline: 0; scrollbar-gutter: auto; }
[data-item-path] { margin-inline: 0; padding-inline: 10px; gap: 10px; }
[data-type="context-menu-anchor"][data-visible="true"] {
  right: calc(10px - var(--trees-focus-ring-width));
}
[data-item-section="content"]:has([data-item-rename-input]) {
  display: flex;
  flex: 1;
  align-items: center;
  height: 100%;
}
[data-item-rename-input] { height: 26px; }
[data-item-type="folder"] > [data-item-section="icon"] > svg { display: none; }
[data-item-type="folder"] > [data-item-section="icon"]::after {
  content: "";
  width: 16px;
  height: 16px;
  background: color-mix(in srgb, var(--z-color-warning) 80%, transparent);
  mask: ${FOLDER_ICON} center / contain no-repeat;
}
`;

type Snapshot = {
  tree: TreePaths;
  folders: readonly Folder[];
  notes: readonly Note[];
  services: Services;
  onOpenNote: (noteId: string) => void;
  onOpenFolder: ((folderId: string) => void) | undefined;
};

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function fail(title: string, err: unknown) {
  toast({ title, description: errorMessage(err), variant: "danger" });
}

/** Directory paths whose parent is the root. */
function rootDirectories(paths: readonly string[]): string[] {
  return paths.filter((p) => p.endsWith("/") && !p.slice(0, -1).includes("/"));
}

/** Every directory the model currently shows expanded, so a reset keeps the user's toggles. */
function directoryAt(model: NotesTreeModel, path: string): FileTreeDirectoryHandle | null {
  const item = model.getItem(path);
  // `isDirectory()` returns a literal but is not a type predicate, so narrow by hand.
  return item?.isDirectory() === true ? (item as FileTreeDirectoryHandle) : null;
}

function expandedDirectories(model: NotesTreeModel, knownPaths: readonly string[]): string[] {
  const out = new Set<string>();
  for (const path of knownPaths) {
    if (path.endsWith("/") && directoryAt(model, path)?.isExpanded()) out.add(path);
  }
  for (const row of model.getVisibleRows(0, model.getVisibleCount())) {
    if (row.kind === "directory" && row.isExpanded) out.add(row.path);
  }
  return [...out];
}

function expandAncestors(model: NotesTreeModel, path: string) {
  for (let dir = parentPathOf(path); dir !== ""; dir = parentPathOf(dir)) {
    const dirItem = directoryAt(model, dir);
    if (dirItem && !dirItem.isExpanded()) dirItem.expand();
  }
}

/**
 * The notes sidebar as an Obsidian-style file tree: folders are directories, notes are `.md`
 * files. Rendered by `@pierre/trees` (virtualized, in a shadow root). Click or Enter opens a
 * note; navigation double-click keeps the tab, while other trees use it to rename. F2 and
 * the context menu rename inline; rows drag into folders or onto the root; the
 * context menu also creates, moves and deletes. Type to search; `ref.openSearch()` does the
 * same from a shortcut.
 */
export function NotesTree({
  selectedNoteId,
  selectedFolderId,
  search = true,
  variant = "default",
  onOpenNote,
  onOpenFolder,
  ref,
  className,
}: NotesTreeProps) {
  const services = useServices();
  const { requestDelete, deleteDialog } = useDeleteConfirmation();
  const store = useStore();
  const expansion = useLiveQuery(["kv"], (s) => s.kv.get<string[]>("notes.expandedFolders"), []);
  const folderQuery = useFolders();
  const noteQuery = useNotes();
  const folders = folderQuery.data ?? [];
  const notes = noteQuery.data ?? [];
  const navigation = variant === "navigation";
  const tree = buildTreePaths(folders, notes, navigation ? "" : LIBRARY_ROOT);

  // The library captures its callbacks once; they read the latest render through this ref.
  const latest = useRef<Snapshot | null>(null);
  const applied = useRef<{ joined: string; paths: readonly string[] } | null>(null);
  const syncingSelection = useRef(false);
  const mutatingTree = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const reorderSources = useRef<readonly string[]>([]);
  const insertionRow = useRef<HTMLElement | null>(null);
  const clearInsertion = () => {
    insertionRow.current?.removeAttribute("data-reorder-edge");
    insertionRow.current = null;
  };
  const pendingRename = useRef<string | null>(null);
  const renamedId = useRef<string | null>(null);
  const pendingExpand = useRef(new Set<string>());
  const [moving, setMoving] = useState<TreeEntry | null>(null);

  const snapshot = (): Snapshot => {
    const s = latest.current;
    if (!s) throw new Error("NotesTree callback fired before mount");
    return s;
  };

  const revert = () => {
    const { tree: current } = snapshot();
    model.resetPaths({
      preparedInput: preparePresortedFileTreeInput(current.paths),
      initialExpandedPaths: expandedDirectories(model, current.paths),
    });
  };

  const handleSelection = (paths: readonly string[]) => {
    if (syncingSelection.current || paths.length !== 1) return;
    const { tree: current, onOpenNote: openNote, onOpenFolder: openFolder } = snapshot();
    const entry = current.byPath.get(paths[0]!);
    if (!entry) return;
    if (entry.kind === "note") openNote(entry.id);
    else openFolder?.(entry.id);
  };

  const handleRename = async (event: FileTreeRenameEvent) => {
    const { tree: current, folders: fs, notes: ns, services: svc } = snapshot();
    const resolved = resolveRename(current.byPath, event);
    if (!resolved) {
      revert();
      return;
    }
    renamedId.current = resolved.id;
    const oldPath = current.pathOf.get(resolved.id);
    if (oldPath) {
      const destination =
        resolved.kind === "folder"
          ? `${event.destinationPath.replace(/\/$/, "")}/`
          : event.destinationPath;
      for (const path of expandedDirectories(model, current.paths)) {
        pendingExpand.current.add(
          path.startsWith(oldPath) ? destination + path.slice(oldPath.length) : path,
        );
      }
      for (let dir = parentPathOf(oldPath); dir; dir = parentPathOf(dir))
        pendingExpand.current.add(dir);
    }
    try {
      if (resolved.kind === "folder") {
        if (fs.find((f) => f.id === resolved.id)?.name === resolved.newName) return revert();
        await svc.folders.rename(resolved.id, resolved.newName);
      } else {
        if (ns.find((n) => n.id === resolved.id)?.title === resolved.newName) return revert();
        await svc.notes.rename(resolved.id, resolved.newName);
      }
    } catch (err) {
      fail("Could not rename", err);
      revert();
    }
  };

  const canDrop = ({ draggedPaths, target }: FileTreeDropContext): boolean => {
    const { tree: current, folders: fs } = snapshot();
    if (mutatingTree.current) return false;
    const destination = target.directoryPath || current.rootPath;
    if (draggedPaths.every((path) => parentPathOf(path) === destination)) return false;
    return draggedPaths.every((path) => {
      const drop = resolveDrop(current.byPath, path, target.directoryPath, current.rootPath);
      if (!drop) return false;
      return drop.kind !== "folder" || !wouldCreateCycle(fs, drop.id, drop.newParentId);
    });
  };

  const handleDrop = async ({ draggedPaths, target }: FileTreeDropContext) => {
    const { tree: current, services: svc } = snapshot();
    if (target.directoryPath) pendingExpand.current.add(target.directoryPath);
    mutatingTree.current = true;
    try {
      for (const path of draggedPaths) {
        if (parentPathOf(path) === (target.directoryPath || current.rootPath)) continue;
        const drop = resolveDrop(current.byPath, path, target.directoryPath, current.rootPath);
        if (!drop) continue;
        try {
          if (drop.kind === "folder") await svc.folders.move(drop.id, drop.newParentId);
          else await svc.notes.move(drop.id, drop.newParentId);
        } catch (err) {
          fail(drop.kind === "folder" ? "Could not move folder" : "Could not move note", err);
          revert();
          return;
        }
      }
    } finally {
      mutatingTree.current = false;
    }
  };

  const { model } = useFileTree({
    preparedInput: preparePresortedFileTreeInput(tree.paths),
    initialExpansion: "closed",
    initialExpandedPaths: rootDirectories(tree.paths),
    flattenEmptyDirectories: false,
    density: "compact",
    ...(navigation ? { itemHeight: 34, stickyFolders: false } : {}),
    unsafeCSS: REORDER_CSS + (navigation ? NAVIGATION_CSS : ""),
    search,
    fileTreeSearchMode: "hide-non-matches",
    icons: { set: "none", colored: false },
    composition: {
      contextMenu: { enabled: true, triggerMode: "both", buttonVisibility: "when-needed" },
    },
    onSelectionChange: handleSelection,
    renaming: {
      canRename: (item) =>
        snapshot().tree.byPath.has(item.path) || snapshot().tree.byPath.has(`${item.path}/`),
      onRename: (event) => void handleRename(event),
      onError: (message) =>
        toast({ title: "Could not rename", description: message, variant: "danger" }),
    },
    dragAndDrop: {
      canDrag: (paths) =>
        !mutatingTree.current && paths.every((path) => snapshot().tree.byPath.has(path)),
      openOnDropDelay: 450,
      canDrop,
      onDropComplete: (event) => void handleDrop(event),
      onDropError: (message) =>
        toast({ title: "Could not move", description: message, variant: "danger" }),
    },
  });
  const visibleCount = useFileTreeSelector(model, (current) => current.getVisibleCount());

  useEffect(() => {
    latest.current = { tree, folders, notes, services, onOpenNote, onOpenFolder };
  });

  // The store is the source of truth; hand the model the new path set whenever it changes.
  useEffect(() => {
    const joined = tree.paths.join("\n");
    const previous = applied.current;
    if (previous === null) {
      applied.current = { joined, paths: tree.paths };
    } else if (previous.joined !== joined) {
      applied.current = { joined, paths: tree.paths };
      syncingSelection.current = true;
      const expanded = [...expandedDirectories(model, previous.paths), ...pendingExpand.current];
      model.resetPaths({
        preparedInput: preparePresortedFileTreeInput(tree.paths),
        initialExpandedPaths: expanded,
      });
      // Explicitly restore directory handles too: prepared tree snapshots may carry their
      // own collapsed projection after a path reset.
      for (const path of expanded) directoryAt(model, path)?.expand();
      pendingExpand.current.clear();
      const renamedPath = renamedId.current ? tree.pathOf.get(renamedId.current) : undefined;
      if (renamedPath) {
        expandAncestors(model, renamedPath);
        model.getItem(renamedPath)?.select();
        model.focusPath(renamedPath);
      }
      renamedId.current = null;
      syncingSelection.current = false;
    }
    const renameId = pendingRename.current;
    const renamePath = renameId ? tree.pathOf.get(renameId) : undefined;
    if (renamePath) {
      pendingRename.current = null;
      model.startRenaming(renamePath);
    }
  }, [model, tree]);

  // Selection from outside (opening a note elsewhere) is mirrored and scrolled into view.
  const selectedId = selectedNoteId ?? selectedFolderId;
  const selectedPath = selectedId ? tree.pathOf.get(selectedId) : undefined;
  useEffect(() => {
    const current = model.getSelectedPaths();
    if (!selectedPath) {
      syncingSelection.current = true;
      for (const path of current) model.getItem(path)?.deselect();
      syncingSelection.current = false;
      return;
    }
    if (current.length === 1 && current[0] === selectedPath) return;
    const item = model.getItem(selectedPath);
    if (!item) return;
    syncingSelection.current = true;
    try {
      for (const path of current) model.getItem(path)?.deselect();
      item.select();
    } finally {
      syncingSelection.current = false;
    }
    expandAncestors(model, selectedPath);
    model.scrollToPath(selectedPath, { focus: false, offset: "nearest" });
  }, [model, selectedPath]);

  const restoreExpansion = useEffectEvent(() => {
    if (!expansion.data) return;
    syncingSelection.current = true;
    for (const path of expandedDirectories(model, tree.paths)) directoryAt(model, path)?.collapse();
    for (const id of expansion.data) {
      const path = tree.pathOf.get(id);
      if (path) directoryAt(model, path)?.expand();
    }
    const activePath =
      (selectedNoteId ?? selectedFolderId)
        ? tree.pathOf.get((selectedNoteId ?? selectedFolderId)!)
        : undefined;
    if (activePath) expandAncestors(model, activePath);
    syncingSelection.current = false;
  });
  const expansionReady = !expansion.isLoading && !folderQuery.isLoading && !noteQuery.isLoading;
  useEffect(() => {
    if (!expansionReady) return;
    restoreExpansion();
    let previous = "";
    const save = () => {
      if (syncingSelection.current) return;
      const current = latest.current?.tree;
      if (!current) return;
      const ids = expandedDirectories(model, current.paths)
        .flatMap((path) => {
          const entry = current.byPath.get(path);
          return entry?.kind === "folder" ? [entry.id] : [];
        })
        .sort();
      const key = JSON.stringify(ids);
      if (key === previous) return;
      previous = key;
      void store.kv.set("notes.expandedFolders", ids);
    };
    return model.subscribe(save);
  }, [model, store, expansionReady]);

  const folderIdFor = (item: ContextMenuItem): string | null => {
    const entry = tree.byPath.get(item.path) ?? tree.byPath.get(`${item.path}/`);
    if (!entry) return null;
    if (entry.kind === "folder") return entry.id;
    const parent = tree.byPath.get(parentPathOf(item.path));
    return parent?.kind === "folder" ? parent.id : null;
  };

  /** Expands a folder on the next path reset, so a new or moved row is visible. */
  const queueExpand = (folderId: string | null) => {
    const path = folderId ? tree.pathOf.get(folderId) : undefined;
    if (path) pendingExpand.current.add(path);
  };

  const newNoteHere = async (item: ContextMenuItem) => {
    const folderId = folderIdFor(item);
    queueExpand(folderId);
    try {
      const note = await services.notes.create(folderId ? { folderId } : {});
      onOpenNote(note.id);
    } catch (err) {
      fail("Could not create note", err);
    }
  };

  const createFolder = async (parentId: string | null) => {
    if (!navigation) directoryAt(model, LIBRARY_ROOT)?.expand();
    queueExpand(parentId);
    try {
      const folder = await services.folders.create(
        parentId ? { name: NEW_FOLDER_NAME, parentId } : { name: NEW_FOLDER_NAME },
      );
      const path = latest.current?.tree.pathOf.get(folder.id);
      if (path && model.getItem(path)) model.startRenaming(path);
      else pendingRename.current = folder.id;
    } catch (err) {
      fail("Could not create folder", err);
    }
  };

  const rootDrop = (): FileTreeDropContext => ({
    draggedPaths: reorderSources.current.filter(
      (path) =>
        !reorderSources.current.some(
          (ancestor) => ancestor !== path && ancestor.endsWith("/") && path.startsWith(ancestor),
        ),
    ),
    target: { kind: "root", directoryPath: null, hoveredPath: null, flattenedSegmentPath: null },
  });

  useImperativeHandle(ref, () => ({
    model,
    createFolder: () => createFolder(null),
    canDropAtRoot: () => reorderSources.current.length > 0 && canDrop(rootDrop()),
    dropAtRoot: async () => {
      if (reorderSources.current.length > 0 && canDrop(rootDrop())) {
        await handleDrop(rootDrop());
        reorderSources.current = [];
      }
    },
    openSearch: (initialValue) => model.openSearch(initialValue),
    startRenaming: (id) => {
      const path = latest.current?.tree.pathOf.get(id);
      return path ? model.startRenaming(path) : false;
    },
  }));

  const deleteEntry = (entry: TreeEntry) => {
    const folder = folders.find((f) => f.id === entry.id);
    const note = notes.find((n) => n.id === entry.id);
    const name = folder?.name ?? (note ? displayTitle(note) : "item");
    requestDelete({
      name,
      description:
        entry.kind === "folder"
          ? "Files and subfolders won't be deleted; they'll appear at the top level. You can restore this folder from Trash."
          : "You can restore this file from Trash.",
      onConfirm: async () => {
        if (entry.kind === "folder") {
          await services.folders.delete(entry.id);
          toast({
            title: `Deleted "${name}"`,
            description: "Its notes now show at the top level.",
            action: { label: "Undo", onClick: () => void services.folders.restore(entry.id) },
          });
        } else {
          await services.notes.delete(entry.id);
          toast({
            title: `Moved "${name}" to Trash`,
            action: { label: "Undo", onClick: () => void services.notes.restore(entry.id) },
          });
        }
      },
    });
  };

  const moveEntry = async (entry: TreeEntry, parentId: string | null) => {
    queueExpand(parentId);
    try {
      if (entry.kind === "folder") await services.folders.move(entry.id, parentId);
      else await services.notes.move(entry.id, parentId);
    } catch (err) {
      fail(entry.kind === "folder" ? "Could not move folder" : "Could not move note", err);
    }
  };

  const reorderFolder = async (id: string, direction: -1 | 1) => {
    if (mutatingTree.current) return;
    const plan = folderReorderPlan(tree, folders, id, direction);
    mutatingTree.current = true;
    try {
      for (const change of plan) await services.folders.reorder(change.id, change.sortOrder);
    } catch (err) {
      fail("Could not reorder folder", err);
      revert();
    } finally {
      mutatingTree.current = false;
    }
  };

  const movingFolder =
    moving?.kind === "folder" ? folders.find((f) => f.id === moving.id) : undefined;
  const movingNote = moving?.kind === "note" ? notes.find((n) => n.id === moving.id) : undefined;

  // Trees renders rows inside a shadow root, so event.target is the host. The composed
  // path identifies the actual row while leaving its disclosure/menu/input controls alone.
  const activationPath = (event: MouseEvent): string | undefined => {
    const elements = event
      .composedPath()
      .filter((node): node is HTMLElement => node instanceof HTMLElement);
    if (
      elements.some((el) =>
        el.matches(
          "input, [data-item-section=action], [data-item-type=folder] > [data-item-section=icon]",
        ),
      )
    )
      return;
    const path = elements
      .find((el) => el.hasAttribute("data-item-path"))
      ?.getAttribute("data-item-path");
    return path && tree.byPath.has(path) ? path : undefined;
  };

  const dragRow = (event: DragEvent) =>
    event
      .composedPath()
      .find(
        (node): node is HTMLElement =>
          node instanceof HTMLElement && node.hasAttribute("data-item-path"),
      );
  const insertion = (event: DragEvent) => {
    if (mutatingTree.current) return;
    const row = dragRow(event);
    const path = row?.getAttribute("data-item-path");
    if (!row || !path) return;
    const rect = row.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const kind = tree.byPath.get(path)?.kind;
    if (kind === "folder" && y > rect.height * 0.25 && y < rect.height * 0.75) return;
    const edge = y < rect.height / 2 ? "before" : "after";
    const entries = siblingReorderPaths(tree, reorderSources.current, path, edge);
    return entries.length ? { row, edge, entries } : undefined;
  };
  const saveOrder = async (entries: TreeEntry[]) => {
    mutatingTree.current = true;
    const entities = entries.map((entry) =>
      (entry.kind === "folder" ? folders : notes).find((item) => item.id === entry.id)!,
    );
    try {
      for (const change of orderChanges(entities)) {
        if (entries[0]?.kind === "folder")
          await services.folders.reorder(change.id, change.sortOrder);
        else await services.notes.reorder(change.id, change.sortOrder);
      }
    } catch (err) {
      fail("Could not reorder", err);
      revert();
    } finally {
      mutatingTree.current = false;
    }
  };

  const onTreeDrag = useEffectEvent((event: DragEvent) => {
    if (event.type === "dragstart") {
      const path = dragRow(event)?.getAttribute("data-item-path");
      const selected = model.getSelectedPaths();
      reorderSources.current = path ? (selected.includes(path) ? selected : [path]) : [];
      return;
    }
    clearInsertion();
    if (event.type === "dragend") {
      reorderSources.current = [];
      return;
    }
    if (event.type === "dragleave") return;
    const drop = insertion(event);
    if (!drop) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "drop") void saveOrder(drop.entries);
    else {
      if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
      drop.row.setAttribute("data-reorder-edge", drop.edge);
      insertionRow.current = drop.row;
    }
  });
  // Trees owns a separate renderer in a shadow root. Capture composed DOM events so edge drops are
  // intercepted before its folder-move handler, without replacing its drag session.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const listener = (event: Event) => onTreeDrag(event as DragEvent);
    root.addEventListener("dragstart", listener, true);
    root.addEventListener("dragover", listener, true);
    root.addEventListener("dragleave", listener, true);
    root.addEventListener("drop", listener, true);
    root.addEventListener("dragend", listener, true);
    return () => {
      root.removeEventListener("dragstart", listener, true);
      root.removeEventListener("dragover", listener, true);
      root.removeEventListener("dragleave", listener, true);
      root.removeEventListener("drop", listener, true);
      root.removeEventListener("dragend", listener, true);
    };
  }, []);

  const onTreeActivation = useEffectEvent((event: MouseEvent) => {
    if (event.type === "click" && event.detail !== 2) return;
    const path = activationPath(event);
    if (!path) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.type !== "dblclick") return;
    const entry = tree.byPath.get(path);
    if (!navigation) model.startRenaming(path);
    else if (entry?.kind === "note") onOpenNote(entry.id, { pinned: true });
    else if (entry?.kind === "folder") onOpenFolder?.(entry.id, { pinned: true });
  });
  // Listen outside the shadow root so Trees cannot consume the second click first.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const listener = (event: MouseEvent) => onTreeActivation(event);
    root.addEventListener("click", listener, true);
    root.addEventListener("dblclick", listener, true);
    return () => {
      root.removeEventListener("click", listener, true);
      root.removeEventListener("dblclick", listener, true);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(navigation ? "relative shrink-0" : "h-full min-h-0", className)}
    >
      <FileTree
        model={model}
        aria-label="Notes"
        className="block min-h-0"
        style={
          navigation
            ? ({
                ...HOST_STYLE,
                height: visibleCount * 34,
                "--trees-font-size-override": "13px",
              } as CSSProperties)
            : search
              ? HOST_STYLE
              : { ...HOST_STYLE, height: Math.min(192, tree.paths.length * 28) }
        }
        renderContextMenu={(item, context) => {
          const entry = tree.byPath.get(item.path) ?? tree.byPath.get(`${item.path}/`);
          const isRoot =
            !navigation && (item.path === LIBRARY_ROOT || item.path === LIBRARY_ROOT.slice(0, -1));
          if (!entry && !isRoot) return null;
          const siblings = entry
            ? tree.paths
                .filter(
                  (path) =>
                    parentPathOf(path) === parentPathOf(item.path) &&
                    tree.byPath.get(path)?.kind === entry.kind,
                )
                .map((path) => tree.byPath.get(path)!.id)
            : [];
          const reorderEntry = (direction: -1 | 1) => {
            if (!entry) return;
            if (entry.kind === "folder") {
              void reorderFolder(entry.id, direction);
              return;
            }
            const neighbor = siblings[siblings.indexOf(entry.id) + direction];
            const path = neighbor ? tree.pathOf.get(neighbor) : undefined;
            if (path)
              void saveOrder(
                siblingReorderPaths(tree, [item.path], path, direction < 0 ? "before" : "after"),
              );
          };
          const index = entry ? siblings.indexOf(entry.id) : -1;
          return (
            <TreeContextMenu
              item={item}
              context={context}
              onOpen={
                entry?.kind === "note"
                  ? (pinned) => onOpenNote(entry.id, { pinned })
                  : entry?.kind === "folder" && onOpenFolder
                    ? (pinned) => onOpenFolder(entry.id, { pinned })
                    : undefined
              }
              onNewNote={() => void newNoteHere(item)}
              onNewFolder={() => void createFolder(folderIdFor(item))}
              onRename={() => {
                context.close({ restoreFocus: false });
                model.startRenaming(item.path);
              }}
              isRoot={isRoot}
              onMove={() => {
                if (entry) setMoving(entry);
              }}
              onDelete={() => {
                if (entry) void deleteEntry(entry);
              }}
              {...(entry
                ? {
                    onMoveUp: index > 0 ? () => reorderEntry(-1) : undefined,
                    onMoveDown: index < siblings.length - 1 ? () => reorderEntry(1) : undefined,
                  }
                : {})}
            />
          );
        }}
      />
      {moving && (
        <FolderPickerDialog
          open
          onOpenChange={(open) => {
            if (!open) setMoving(null);
          }}
          title={`Move "${movingFolder?.name ?? (movingNote ? displayTitle(movingNote) : "")}"`}
          description="Choose the folder it should live in."
          currentId={movingFolder?.parentId ?? movingNote?.folderId ?? null}
          {...(movingFolder ? { excludeIds: descendantFolderIds(folders, movingFolder.id) } : {})}
          onPick={(parentId) => void moveEntry(moving, parentId)}
        />
      )}
      {deleteDialog}
    </div>
  );
}

type TreeContextMenuProps = {
  item: ContextMenuItem;
  context: ContextMenuOpenContext;
  onOpen?: ((pinned: boolean) => void) | undefined;
  onNewNote: () => void;
  onNewFolder: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  isRoot: boolean;
  onMoveUp?: (() => void) | undefined;
  onMoveDown?: (() => void) | undefined;
};

/**
 * Our dropdown menu anchored where the tree opened its context menu. The tree owns the open
 * state: closing here calls `context.close()`, which restores focus to the row; Rename closes
 * first without restoring focus so the inline input keeps it.
 */
function TreeContextMenu({
  item,
  context,
  onOpen,
  onNewNote,
  onNewFolder,
  onRename,
  onMove,
  onDelete,
  isRoot,
  onMoveUp,
  onMoveDown,
}: TreeContextMenuProps) {
  const closed = useRef(false);
  const closeOnce = (options?: { restoreFocus?: boolean }) => {
    if (closed.current) return;
    closed.current = true;
    context.close(options);
  };
  const isFolder = item.kind === "directory";
  return (
    <DropdownMenu.Root
      open
      onOpenChange={(open) => {
        if (!open) closeOnce();
      }}
    >
      <DropdownMenu.Trigger
        nativeButton={false}
        render={<span aria-hidden className="absolute inset-0 size-full" />}
      />
      <DropdownMenu.Content
        data-file-tree-context-menu-root="true"
        aria-label={`Actions for ${item.name}`}
        finalFocus={false}
        align="start"
        sideOffset={2}
      >
        {onOpen && (
          <>
            <DropdownMenu.Item onClick={() => onOpen(false)}>Open</DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => onOpen(true)}>
              <PanelsTopLeft /> Open in new tab
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
          </>
        )}
        <DropdownMenu.Item onClick={onNewNote}>
          <FilePlus /> New note here
        </DropdownMenu.Item>
        <DropdownMenu.Item onClick={onNewFolder}>
          <FolderPlus /> {isFolder && !isRoot ? "New subfolder" : "New folder here"}
        </DropdownMenu.Item>
        {!isRoot && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Item
              onClick={() => {
                closed.current = true;
                onRename();
              }}
            >
              <Pencil /> Rename
              <DropdownMenu.Shortcut keys="F2" />
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={onMove}>
              <FolderInput /> Move to…
            </DropdownMenu.Item>
            <>
              <DropdownMenu.Separator />
              <DropdownMenu.Item disabled={!onMoveUp} onClick={onMoveUp}>
                <ArrowUp /> Move up
              </DropdownMenu.Item>
              <DropdownMenu.Item disabled={!onMoveDown} onClick={onMoveDown}>
                <ArrowDown /> Move down
              </DropdownMenu.Item>
            </>
            <DropdownMenu.Separator />
            <DropdownMenu.Item variant="destructive" onClick={onDelete}>
              <Trash2 /> {isFolder ? "Delete folder" : "Move to Trash"}
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
